import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, getUser } from '../api';
import { LoadingSpinner, ConfirmDialog } from './shared';

const icona = (v) => {
  if (v.cartella) return '📁';
  const e = (v.nome.split('.').pop() || '').toLowerCase();
  if (e === 'pdf') return '📄';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(e)) return '🖼️';
  if (['doc', 'docx', 'odt', 'rtf'].includes(e)) return '📝';
  if (['xls', 'xlsx', 'ods', 'csv'].includes(e)) return '📊';
  if (['zip', 'rar', '7z'].includes(e)) return '🗜️';
  if (['eml', 'msg'].includes(e)) return '✉️';
  return '📎';
};
const dimensione = (n) => {
  if (n == null || n < 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1048576) return Math.round(n / 1024) + ' KB';
  return (n / 1048576).toFixed(1).replace('.', ',') + ' MB';
};
const dataIt = (s) => { try { return s ? new Date(s).toLocaleDateString('it-IT') : ''; } catch (e) { return ''; } };

export default function DocumentiCliente({ clienteId, addToast }) {
  const [collegate, setCollegate] = useState(null);   // [{anno, percorso}]
  const [sel, setSel] = useState(null);               // 'GENERALE' | '2025'
  const [sub, setSub] = useState('');
  const [voci, setVoci] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [invio, setInvio] = useState(false);
  const [trascina, setTrascina] = useState(false);
  const [daEliminare, setDaEliminare] = useState(null);
  const fileRef = useRef();
  const puoScrivere = (getUser() || {}).ruolo !== 'readonly';

  // cartelle collegate al cliente
  useEffect(() => {
    api.get(`/api/clienti/${clienteId}/cartelle`)
      .then(d => {
        const c = d.collegamenti || [];
        setCollegate(c);
        const chiavi = c.map(x => (x.anno === 0 ? 'GENERALE' : String(x.anno)));
        const anno = String(new Date().getFullYear());
        setSel(chiavi.includes(anno) ? anno : (chiavi.find(k => k !== 'GENERALE') || chiavi[0] || null));
      })
      .catch(e => { setCollegate([]); setErrore(e.message); })
      .finally(() => setLoading(false));
  }, [clienteId]);

  const carica = useCallback(() => {
    if (!sel) return;
    setLoading(true); setErrore('');
    api.get(`/api/clienti/${clienteId}/documenti?anno=${encodeURIComponent(sel)}&sub=${encodeURIComponent(sub)}`)
      .then(d => setVoci(d.voci || []))
      .catch(e => { setVoci([]); setErrore(e.message); })
      .finally(() => setLoading(false));
  }, [clienteId, sel, sub]);
  useEffect(() => { carica(); }, [carica]);

  const cambiaBase = (k) => { setSel(k); setSub(''); };
  const vai = (nome) => setSub(sub ? sub + '/' + nome : nome);
  const parti = sub ? sub.split('/') : [];

  // apre o scarica un file: chiede un link temporaneo e lo apre in una nuova scheda
  const apri = async (v, scarica) => {
    const w = scarica ? null : window.open('', '_blank');
    try {
      const r = await api.post(`/api/clienti/${clienteId}/documenti/link`, { anno: sel, sub, nome: v.nome });
      const url = r.url + (scarica ? '?download=1' : '');
      if (scarica) { const a = document.createElement('a'); a.href = url; a.download = v.nome; document.body.appendChild(a); a.click(); a.remove(); }
      else if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { if (w) w.close(); addToast(e.message, 'error'); }
  };

  const carcaFile = async (files) => {
    if (!files || !files.length) return;
    setInvio(true);
    try {
      const fd = new FormData();
      fd.append('anno', sel); fd.append('sub', sub);
      [...files].forEach(f => fd.append('file', f));
      const res = await fetch(`/api/clienti/${clienteId}/documenti`, {
        method: 'POST', body: fd, headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') },
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Caricamento non riuscito');
      addToast(j.data.caricati.length === 1 ? `"${j.data.caricati[0]}" caricato` : `${j.data.caricati.length} file caricati`, 'success');
      carica();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setInvio(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const nuovaCartella = async () => {
    const nome = window.prompt('Nome della nuova cartella:');
    if (!nome || !nome.trim()) return;
    try { await api.post(`/api/clienti/${clienteId}/documenti/cartella`, { anno: sel, sub, nome: nome.trim() }); carica(); }
    catch (e) { addToast(e.message, 'error'); }
  };

  const elimina = async () => {
    const v = daEliminare; setDaEliminare(null);
    try { await api.post(`/api/clienti/${clienteId}/documenti/elimina`, { anno: sel, sub, nome: v.nome }); addToast('File eliminato', 'success'); carica(); }
    catch (e) { addToast(e.message, 'error'); }
  };

  if (collegate === null) return <LoadingSpinner />;
  if (!collegate.length) {
    return (
      <div className="empty-state" style={{ padding:'40px 0' }}>
        <div className="empty-state-icon">☁</div>
        <div className="empty-state-title">Nessuna cartella OneDrive collegata</div>
        <div style={{ fontSize:13, color:'var(--text-faint)', marginTop:4 }}>
          Collega le cartelle di questo cliente dalla scheda <b>☁ Cartelle</b>.
        </div>
        {errore && <div style={{ color:'#DC2626', fontSize:12, marginTop:8 }}>{errore}</div>}
      </div>
    );
  }

  const basi = collegate.map(x => [x.anno === 0 ? 'GENERALE' : String(x.anno), x.anno === 0 ? 'Generale' : String(x.anno), x.percorso]);
  basi.sort((a, b) => (a[0] === 'GENERALE' ? -1 : b[0] === 'GENERALE' ? 1 : b[0].localeCompare(a[0])));

  return (
    <div onDragOver={e => { if (puoScrivere) { e.preventDefault(); setTrascina(true); } }}
         onDragLeave={() => setTrascina(false)}
         onDrop={e => { e.preventDefault(); setTrascina(false); if (puoScrivere) carcaFile(e.dataTransfer.files); }}
         style={{ outline: trascina ? '2px dashed var(--accent)' : 'none', borderRadius:'var(--radius-sm)' }}>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
        {basi.map(([k, titolo]) => (
          <button key={k} onClick={() => cambiaBase(k)} className="btn btn-sm"
            style={{ background: sel === k ? 'var(--accent)' : undefined, color: sel === k ? 'white' : undefined, fontWeight: sel === k ? 600 : 400 }}>
            {titolo}
          </button>
        ))}
        <span style={{ flex:1 }} />
        {puoScrivere && <>
          <input ref={fileRef} type="file" multiple style={{ display:'none' }} onChange={e => carcaFile(e.target.files)} />
          <button className="btn btn-sm" onClick={nuovaCartella}>＋ Nuova cartella</button>
          <button className="btn btn-sm btn-primary" disabled={invio} onClick={() => fileRef.current.click()}>
            {invio ? '⏳ Caricamento…' : '⬆ Carica file'}
          </button>
        </>}
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap', fontSize:13, marginBottom:10 }}>
        <button className="btn btn-sm" onClick={() => setSub('')}>📁 {basi.find(b => b[0] === sel)?.[2] || ''}</button>
        {parti.map((p, i) => (
          <React.Fragment key={i}>
            <span style={{ color:'var(--text-faint)' }}>/</span>
            <button className="btn btn-sm" onClick={() => setSub(parti.slice(0, i + 1).join('/'))}>{p}</button>
          </React.Fragment>
        ))}
        <button className="btn btn-sm" style={{ marginLeft:'auto' }} onClick={carica} title="Ricarica">↻</button>
      </div>

      {errore && <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius-sm)', color:'#DC2626', fontSize:13, marginBottom:10 }}>⚠ {errore}</div>}

      {loading ? <LoadingSpinner /> : (
        <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-sm)' }}>
          {sub && (
            <div onClick={() => setSub(parti.slice(0, -1).join('/'))}
              style={{ padding:'9px 14px', borderBottom:'1px solid var(--border)', cursor:'pointer', fontSize:13.5, color:'var(--text-muted)' }}>
              ↩ Indietro
            </div>
          )}
          {voci.length === 0 && !errore && (
            <div style={{ padding:'30px 0', textAlign:'center', color:'var(--text-faint)', fontSize:13 }}>
              Cartella vuota{puoScrivere ? ' — trascina qui i file per caricarli' : ''}
            </div>
          )}
          {voci.map(v => (
            <div key={v.nome} style={{ display:'flex', alignItems:'center', gap:12, padding:'9px 14px', borderBottom:'1px solid var(--border)' }}>
              <span style={{ fontSize:20, flexShrink:0 }}>{icona(v)}</span>
              <div onClick={() => (v.cartella ? vai(v.nome) : apri(v, false))}
                style={{ flex:1, minWidth:0, cursor:'pointer', fontSize:13.5, fontWeight: v.cartella ? 600 : 400, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}
                title={v.nome}>
                {v.nome}
              </div>
              <span style={{ fontSize:12, color:'var(--text-faint)', width:70, textAlign:'right' }}>{dimensione(v.dimensione)}</span>
              <span style={{ fontSize:12, color:'var(--text-faint)', width:80, textAlign:'right' }}>{dataIt(v.modificato)}</span>
              {!v.cartella && (
                <div style={{ display:'flex', gap:4 }}>
                  <button className="btn btn-sm" onClick={() => apri(v, false)}>Apri</button>
                  <button className="btn btn-sm" onClick={() => apri(v, true)} title="Scarica">⬇</button>
                  {puoScrivere && <button className="btn btn-sm btn-danger" onClick={() => setDaEliminare(v)} title="Elimina">×</button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {daEliminare && (
        <ConfirmDialog message={`Eliminare "${daEliminare.nome}" da OneDrive? (resta nel cestino di OneDrive)`}
          onConfirm={elimina} onCancel={() => setDaEliminare(null)} />
      )}
    </div>
  );
}
